import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ReviewStatusBadge } from "./ReviewStatusBadge";
import { Eye, Edit, Copy, Trash2, Brain, Sparkles, ChevronDown, ChevronUp, ArrowUpDown, CheckCircle2, XCircle, History } from "lucide-react";
import { MCQVersionHistory } from "./MCQVersionHistory";
import { MCQLifecycleControl, type LifecycleStatus } from "./MCQLifecycleControl";
import { cn } from "@/lib/utils";

type Mcq = {
  id: string;
  topic_id: string | null;
  question: string;
  options: string[];
  correct_index: number;
  explanation?: string | null;
  difficulty: number;
  tags: string[] | null;
  review_status?: 'pending' | 'approved' | 'needs_revision';
  last_reviewed_at?: string | null;
  lifecycle_status?: LifecycleStatus | null;
  retired_reason?: string | null;
  exam_labels?: Array<{
    id: string;
    name: string;
    slug: string;
    color: string;
  }>;
};

type MCQTableProps = {
  mcqs: Mcq[];
  topics: Map<string, { name: string }>;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onEdit: (mcq: Mcq) => void;
  onPreview: (mcq: Mcq) => void;
  onDuplicate: (mcq: Mcq) => void;
  onDelete: (mcq: Mcq) => void;
  onGenerateExplanation: (mcq: Mcq) => void;
  onAIReview: (mcq: Mcq) => void;
};

type SortField = 'question' | 'topic' | 'exam_labels' | 'difficulty' | 'review_status';
type SortOrder = 'asc' | 'desc';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'approved': return 'border-l-green-500';
    case 'needs_revision': return 'border-l-yellow-500';
    case 'pending': return 'border-l-gray-400';
    default: return 'border-l-gray-300';
  }
};

const DifficultyStars = ({ difficulty }: { difficulty: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={cn("text-lg", i < difficulty ? "text-yellow-500" : "text-muted")}>
        ★
      </span>
    ))}
  </div>
);

export const MCQTable = ({ mcqs, topics, selectedIds, onSelectionChange, onEdit, onPreview, onDuplicate, onDelete, onGenerateExplanation, onAIReview }: MCQTableProps) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('question');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [historyMcqId, setHistoryMcqId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedMcqs = [...mcqs].sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'question':
        comparison = a.question.localeCompare(b.question);
        break;
      case 'topic':
        const topicA = a.topic_id ? topics.get(a.topic_id)?.name || '' : '';
        const topicB = b.topic_id ? topics.get(b.topic_id)?.name || '' : '';
        comparison = topicA.localeCompare(topicB);
        break;
      case 'exam_labels':
        const labelsA = a.exam_labels?.map(l => l.name).join(', ') || '';
        const labelsB = b.exam_labels?.map(l => l.name).join(', ') || '';
        comparison = labelsA.localeCompare(labelsB);
        break;
      case 'difficulty':
        comparison = a.difficulty - b.difficulty;
        break;
      case 'review_status':
        comparison = (a.review_status || 'pending').localeCompare(b.review_status || 'pending');
        break;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1 text-muted-foreground" />;
    return sortOrder === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />;
  };

  return (
    <>
    <div className="relative w-full overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px] sticky top-0 bg-background z-10">
              <Checkbox
                checked={selectedIds.size === sortedMcqs.length && sortedMcqs.length > 0}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onSelectionChange(new Set(sortedMcqs.map(mcq => mcq.id)));
                  } else {
                    onSelectionChange(new Set());
                  }
                }}
                aria-label="Select all"
              />
            </TableHead>
            <TableHead className="w-[35%] sticky top-0 bg-background z-10">
              <Button variant="ghost" size="sm" onClick={() => handleSort('question')} className="h-8 px-2">
                Question
                <SortIcon field="question" />
              </Button>
            </TableHead>
            <TableHead className="w-[12%] sticky top-0 bg-background z-10">
              <Button variant="ghost" size="sm" onClick={() => handleSort('topic')} className="h-8 px-2">
                Topic
                <SortIcon field="topic" />
              </Button>
            </TableHead>
            <TableHead className="w-[13%] sticky top-0 bg-background z-10">
              <Button variant="ghost" size="sm" onClick={() => handleSort('exam_labels')} className="h-8 px-2">
                Exam Labels
                <SortIcon field="exam_labels" />
              </Button>
            </TableHead>
            <TableHead className="w-[8%] sticky top-0 bg-background z-10">
              <Button variant="ghost" size="sm" onClick={() => handleSort('difficulty')} className="h-8 px-2">
                Difficulty
                <SortIcon field="difficulty" />
              </Button>
            </TableHead>
            <TableHead className="w-[10%] sticky top-0 bg-background z-10">
              <Button variant="ghost" size="sm" onClick={() => handleSort('review_status')} className="h-8 px-2">
                Status
                <SortIcon field="review_status" />
              </Button>
            </TableHead>
            <TableHead className="w-[22%] sticky top-0 bg-background z-10">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedMcqs.map((mcq) => {
            const isExpanded = expandedRows.has(mcq.id);
            const questionPreview = mcq.question.slice(0, 150);
            const needsTruncate = mcq.question.length > 150;
            
            return (
              <TableRow 
                key={mcq.id} 
                className={cn(
                  "border-l-4", 
                  getStatusColor(mcq.review_status || 'pending'),
                  selectedIds.has(mcq.id) && "bg-accent/50"
                )}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(mcq.id)}
                    onCheckedChange={(checked) => {
                      const newSelected = new Set(selectedIds);
                      if (checked) {
                        newSelected.add(mcq.id);
                      } else {
                        newSelected.delete(mcq.id);
                      }
                      onSelectionChange(newSelected);
                    }}
                    aria-label={`Select ${mcq.question.slice(0, 50)}...`}
                  />
                </TableCell>
                <TableCell className="py-3">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <p className={cn("text-sm", !isExpanded && needsTruncate && "line-clamp-2")}>
                          {isExpanded ? mcq.question : questionPreview}
                          {!isExpanded && needsTruncate && '...'}
                        </p>
                        {needsTruncate && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpand(mcq.id)}
                            className="h-6 px-2 mt-1 text-xs text-primary"
                          >
                            {isExpanded ? (
                              <>Show less <ChevronUp className="h-3 w-3 ml-1" /></>
                            ) : (
                              <>Show more <ChevronDown className="h-3 w-3 ml-1" /></>
                            )}
                          </Button>
                        )}
                      </div>
                      {mcq.explanation ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>
                    
                    {isExpanded && (
                      <div className="space-y-2 pt-2 border-t">
                        <p className="text-xs font-medium text-muted-foreground">Options:</p>
                        <div className="grid gap-1">
                          {mcq.options.map((opt, idx) => (
                            <div
                              key={idx}
                              className={cn(
                                "text-sm px-2 py-1 rounded border",
                                idx === mcq.correct_index
                                  ? "border-green-500 bg-green-50 dark:bg-green-950 font-medium"
                                  : "border-border"
                              )}
                            >
                              {String.fromCharCode(65 + idx)}. {opt}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {(mcq.tags && mcq.tags.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {mcq.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {mcq.tags.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{mcq.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </TableCell>
                
                <TableCell className="text-sm text-muted-foreground">
                  {mcq.topic_id ? topics.get(mcq.topic_id)?.name ?? "—" : "—"}
                </TableCell>
                
                <TableCell>
                  {mcq.exam_labels && mcq.exam_labels.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {mcq.exam_labels.map((label) => (
                        <Badge 
                          key={label.id} 
                          variant="outline"
                          style={{ 
                            borderColor: label.color,
                            color: label.color 
                          }}
                          className="text-xs"
                        >
                          {label.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                
                <TableCell>
                  <DifficultyStars difficulty={mcq.difficulty} />
                </TableCell>
                
                <TableCell>
                  <div className="space-y-1">
                    <ReviewStatusBadge status={mcq.review_status || 'pending'} />
                    <MCQLifecycleControl
                      mcqId={mcq.id}
                      status={(mcq.lifecycle_status as LifecycleStatus) || 'active'}
                      retiredReason={mcq.retired_reason}
                    />
                    {mcq.last_reviewed_at && (
                      <div className="text-xs text-muted-foreground">
                        {new Date(mcq.last_reviewed_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onPreview(mcq)}
                      className="h-8 px-2"
                      title="Preview"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(mcq)}
                      className="h-8 px-2"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDuplicate(mcq)}
                      className="h-8 px-2"
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onAIReview(mcq)}
                      className="h-8 px-2 text-primary"
                      title="AI Review"
                    >
                      <Sparkles className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onGenerateExplanation(mcq)}
                      className="h-8 px-2"
                      title={mcq.explanation ? "Update Explanation" : "Generate Explanation"}
                    >
                      <Brain className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistoryMcqId(mcq.id)}
                      className="h-8 px-2"
                      title="Version History"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-destructive hover:text-destructive"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete MCQ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(mcq)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
    {historyMcqId && (
      <MCQVersionHistory
        mcqId={historyMcqId}
        open={!!historyMcqId}
        onOpenChange={(o) => !o && setHistoryMcqId(null)}
      />
    )}
    </>
  );
};
